import { useState } from 'react';
import { Button, Divider, Form, Input, Modal, Radio, Statistic } from 'antd';
import { MailOutlined, PhoneOutlined, EditOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { useTranslation } from 'react-i18next';
import { updateContactInfoApi, updateContactInfoResendCodeApi, updateContactInfoVerifyApi } from '@http/user';

const { Countdown } = Statistic;

export const ContactInfo = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.appReducer.user);
  const [form] = Form.useForm();
  const [otpForm] = Form.useForm();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [otpState, setOtpState] = useState(false);
  const [time, setTime] = useState(false);
  const [deadline, setDeadline] = useState(0);

  const openModal = () => {
    form.setFieldsValue({
      otp_delivery: 'sms',
      phone_number: user.mobile_phone,
      email: user.email,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setOtpState(false);
    setTime(false);
    form.resetFields();
    otpForm.resetFields();
  };

  const updateContactInfo = (values: any) => {
    dispatch(updateContactInfoApi(values)).then(data => {
      if (data) {
        setDeadline(Date.now() + 1000 * 60);
        setTime(false);
        setOtpState(true);
      }
    });
  };

  const confirmOtp = (values: any) => {
    dispatch(updateContactInfoVerifyApi({ code: values.code }, closeModal));
    otpForm.resetFields();
  };

  const resendCode = () => {
    setDeadline(Date.now() + 1000 * 60);
    setTime(false);
    dispatch(updateContactInfoResendCodeApi());
  };

  const onFinishCountDown = () => {
    setTime(true);
  };

  const contacts = [
    { key: 'phone', icon: <PhoneOutlined />, label: t('Phone label'), value: user.mobile_phone },
    { key: 'email', icon: <MailOutlined />, label: t('Email'), value: user.email },
  ];

  return (
    <div>
      <div className='p-4 rounded-xl' style={{ background: '#fafafa', border: '1px solid #f0f0f0' }}>
        <div className='flex items-center justify-between gap-3'>
          <div className='min-w-0'>
            <p className='text-sm font-medium m-0' style={{ color: '#011957' }}>
              {t('Contact info')}
            </p>
            <p className='text-xs text-gray-400 m-0 mt-0.5'>{t('Contact info desc')}</p>
          </div>
          <Button icon={<EditOutlined />} onClick={openModal}>
            {t('Change')}
          </Button>
        </div>

        <Divider className='my-4' />

        <div className='space-y-3'>
          {contacts.map(contact => (
            <div key={contact.key} className='flex items-center gap-3'>
              <div
                className='w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0'
                style={{ background: '#f0f4ff' }}
              >
                <span style={{ color: '#011957', fontSize: 13 }}>{contact.icon}</span>
              </div>
              <div className='min-w-0'>
                <p className='text-xs text-gray-400 m-0'>{contact.label}</p>
                <p className='text-sm font-medium m-0 truncate'>{contact.value || '—'}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Modal title={t('Change contact info')} open={isModalOpen} onCancel={closeModal} footer={null}>
        <Form form={form} className='mt-5' name='contactInfo' onFinish={updateContactInfo} autoComplete='off'>
          <div className='mb-5'>
            <p className='text-sm text-slate-500 mb-3'>{t('otpType')}</p>
            <Form.Item className='m-0' name='otp_delivery' rules={[{ required: true, message: `${t('Required')}` }]}>
              <Radio.Group disabled={otpState}>
                <Radio value={'sms'}>{t('withSMS')}</Radio>
                <Radio value={'email'}>{t('withEmail')}</Radio>
              </Radio.Group>
            </Form.Item>
          </div>

          <Form.Item name='phone_number' rules={[{ required: true, message: `${t('Required')}` }]}>
            <Input
              size='large'
              disabled={otpState}
              placeholder={`${t('Enter phone number')}`}
              prefix={<PhoneOutlined className='text-gray-300' />}
            />
          </Form.Item>

          <Form.Item name='email' rules={[{ type: 'email', message: `${t('Invalid email')}` }]}>
            <Input
              size='large'
              disabled={otpState}
              placeholder={`${t('Enter email')}`}
              prefix={<MailOutlined className='text-gray-300' />}
            />
          </Form.Item>

          {!otpState ? (
            <div className='flex justify-end mt-5'>
              <Button className='mr-3' onClick={closeModal}>
                {t('Cancel')}
              </Button>
              <Button type='primary' htmlType='submit'>
                {t('Change')}
              </Button>
            </div>
          ) : null}
        </Form>

        {otpState ? (
          <div>
            <Divider />
            <p className='text-base'>{t('OtpCode')}</p>
            <span className='text-xs text-slate-400'>{t('ConfirmContactInfoOtp')}</span>

            <Form form={otpForm} className='mt-3' name='contactInfoOtp' onFinish={confirmOtp} autoComplete='off'>
              <Form.Item className='m-0' name='code' rules={[{ required: true, message: `${t('Required')}` }]}>
                <Input
                  placeholder={`${t('Enter SMS code')}`}
                  size='large'
                  style={{ width: '100%' }}
                  type='text'
                  maxLength={6}
                />
              </Form.Item>

              {!time ? (
                <div className='mt-3 flex items-center text-sm text-slate-400'>
                  <span className='mr-2'>{t('ResendTime')}</span>
                  <Countdown
                    className='!text-xs'
                    style={{ fontSize: 12 }}
                    value={deadline}
                    onFinish={onFinishCountDown}
                    format='mm:ss'
                  />
                  <span className='ml-2'> {t('Second')}!</span>
                </div>
              ) : null}

              <div className='flex justify-end mt-5'>
                {time ? (
                  <Button className='mr-3' onClick={resendCode}>
                    {t('ResendCode')}
                  </Button>
                ) : null}
                <Button type='primary' htmlType='submit'>
                  {t('Confirm')}
                </Button>
              </div>
            </Form>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default ContactInfo;
